import { createContext, type PropsWithChildren, useContext } from "react";
import type { User } from "../types/models/User.types";
import type { AuthUser } from "../types/api/user.types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import getMe from "$/api/auth/get-me";

type UserContextType = AuthUser;

type AuthContextType = {
  user: UserContextType | null;
  isLoading: boolean;
  setUser: (user: UserContextType) => void;
  clearUser: () => void;
  invalidateUser: () => void;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: false,
  setUser: () => {},
  clearUser: () => {},
  invalidateUser: () => {},
});

const AuthProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();

  const { data: user, isLoading } = useQuery({
    queryKey: ["user"],
    queryFn: getMe,
    // TODO: check if this is needed
    refetchInterval: 30 * 1000,
  });

  const handleInvalidateUser = () => {
    queryClient.invalidateQueries({ queryKey: ["user"] });
  };

  const handleSetUser = (user: User) => {
    queryClient.setQueryData(["user"], user);
  };

  const handleClearUser = () => {
    queryClient.setQueryData(["user"], null);
  };

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        setUser: handleSetUser,
        clearUser: handleClearUser,
        invalidateUser: handleInvalidateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
